<template>
  <div class="relation-types-list">
    <div v-if="loading" class="muted" style="margin-bottom: 12px">{{ __('Loading…') }}</div>
    <div v-else-if="relationTypes.length === 0" class="muted" style="margin-bottom: 12px">
      {{ s__('ProjectSettings|No relation types defined yet.') }}
    </div>
    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
      <li v-for="relationType in relationTypes" :key="relationType.id" class="relation-type-item">
        <div class="relation-type-main">
          <div class="relation-type-header">
            <span v-if="relationType.icon" class="relation-icon">{{ relationType.icon }}</span>
            <span
              class="relation-badge relation-direct-badge"
              :style="{ backgroundColor: relationType.color || '#6b7280' }"
            >
              {{ relationType.directTitle }}
            </span>
            <span class="relation-reverse-line">
              <span class="muted relation-link-arrow">↔</span>
              <span
                class="relation-badge relation-reverse-badge"
                :style="{ backgroundColor: relationType.color || '#6b7280' }"
              >
                {{ relationType.reverseTitle }}
              </span>
            </span>
          </div>
          <div class="muted relation-count">
            {{
              n__(
                'ProjectSettings|%d relation',
                'ProjectSettings|%d relations',
                relationType.horizontalRelationsCount
              ).replace('%d', relationType.horizontalRelationsCount)
            }}
          </div>
        </div>
        <div class="relation-type-actions">
          <button class="btn-sm" @click="editRelationType(relationType)">
            <span class="btn-text">{{ s__('ProjectSettings|Edit') }}</span>
            <span><EditIcon /></span>
          </button>
          <button class="btn-sm btn-danger" @click="deleteRelationType(relationType)">
            <span class="btn-text">{{ s__('ProjectSettings|Delete') }}</span>
            <span><TrashIcon /></span>
          </button>
        </div>
      </li>
    </ul>

    <button v-if="showCreateButton && !showCreateModal && !showEditModal" @click="openCreateModal">
      {{ s__('ProjectSettings|Add Relation Type') }}
    </button>

    <div v-if="showCreateModal" class="modal-overlay">
      <div class="modal-content relation-type-modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Add Relation Type') }}</h3>
          <button type="button" class="modal-close" aria-label="Close" @click="cancelNew">×</button>
        </div>
        <div class="modal-body">
          <div class="relation-type-form">
            <div class="row">
              <div style="flex: 1">
                <label>{{ s__('ProjectSettings|Direct Title') }}</label>
                <input
                  v-model="newRelationType.directTitle"
                  type="text"
                  :placeholder="s__('ProjectSettings|e.g., Blocks')"
                />
              </div>
              <div style="flex: 1">
                <label>{{ s__('ProjectSettings|Reverse Title') }}</label>
                <input
                  v-model="newRelationType.reverseTitle"
                  type="text"
                  :placeholder="s__('ProjectSettings|e.g., Blocked by')"
                />
              </div>
            </div>
            <div class="row" style="margin-top: 12px">
              <div style="flex: 0 0 150px">
                <label>{{ s__('ProjectSettings|Color') }}</label>
                <input v-model="newRelationType.color" type="color" />
              </div>
              <div style="flex: 1">
                <label>{{ s__('ProjectSettings|Icon (optional)') }}</label>
                <div class="icon-picker">
                  <div class="icon-gallery">
                    <button
                      v-for="icon in iconOptions"
                      :key="icon"
                      type="button"
                      class="icon-option"
                      :class="{ selected: newRelationType.icon === icon }"
                      @click="newRelationType.icon = newRelationType.icon === icon ? '' : icon"
                    >
                      {{ icon }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="mobile-stretch-btn-group" style="display: flex; gap: 8px; margin-top: 12px">
              <button
                :disabled="!canCreate || submitting"
                class="btn-primary"
                @click="createRelationType"
              >
                {{
                  submitting ? s__('ProjectSettings|Creating...') : s__('ProjectSettings|Create')
                }}
              </button>
              <button class="btn-secondary" @click="cancelNew">{{ __('Cancel') }}</button>
            </div>
            <div v-if="error" class="error" style="margin-top: 8px">{{ error }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showEditModal && editingRelationType" class="modal-overlay">
      <div class="modal-content relation-type-modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Edit Relation Type') }}</h3>
          <button type="button" class="modal-close" aria-label="Close" @click="cancelEdit">
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="relation-type-form">
            <div class="row">
              <div style="flex: 1">
                <label>{{ s__('ProjectSettings|Direct Title') }}</label>
                <input
                  v-model="editingRelationType.directTitle"
                  type="text"
                  :placeholder="s__('ProjectSettings|e.g., Blocks')"
                />
              </div>
              <div style="flex: 1">
                <label>{{ s__('ProjectSettings|Reverse Title') }}</label>
                <input
                  v-model="editingRelationType.reverseTitle"
                  type="text"
                  :placeholder="s__('ProjectSettings|e.g., Blocked by')"
                />
              </div>
            </div>
            <div class="row" style="margin-top: 12px">
              <div style="flex: 0 0 150px">
                <label>{{ s__('ProjectSettings|Color') }}</label>
                <input v-model="editingRelationType.color" type="color" />
              </div>
              <div style="flex: 1">
                <label>{{ s__('ProjectSettings|Icon (optional)') }}</label>
                <div class="icon-picker">
                  <div class="icon-gallery">
                    <button
                      v-for="icon in iconOptions"
                      :key="icon"
                      type="button"
                      class="icon-option"
                      :class="{ selected: editingRelationType.icon === icon }"
                      @click="
                        editingRelationType.icon = editingRelationType.icon === icon ? '' : icon
                      "
                    >
                      {{ icon }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="mobile-stretch-btn-group" style="display: flex; gap: 8px; margin-top: 12px">
              <button
                :disabled="!canUpdate || submitting"
                class="btn-primary"
                @click="updateRelationType"
              >
                {{ submitting ? s__('ProjectSettings|Saving...') : s__('ProjectSettings|Save') }}
              </button>
              <button class="btn-secondary" @click="cancelEdit">{{ __('Cancel') }}</button>
            </div>
            <div v-if="error" class="error" style="margin-top: 8px">{{ error }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { s__, n__, __ } from '@/locale'
import EditIcon from '../common/icons/EditIcon.vue'
import TrashIcon from '../common/icons/TrashIcon.vue'

const props = defineProps({
  projectId: { type: String, required: true },
  expanded: { type: Boolean, default: false },
  showCreateButton: { type: Boolean, default: true },
})

const emit = defineEmits(['update:expanded'])

const relationTypes = ref([])
const showCreateModal = ref(false)
const showEditModal = ref(false)
const editingRelationType = ref(null)
const submitting = ref(false)
const error = ref('')

const newRelationType = ref({
  directTitle: '',
  reverseTitle: '',
  color: '#6b7280',
  icon: '',
})

// Icon options for the picker
const iconOptions = [
  '🔗',
  '⛔',
  '🚫',
  '↔️',
  '➡️',
  '⬅️',
  '🔄',
  '✅',
  '⚡',
  '📎',
  '🔀',
  '⏭️',
  '⏮️',
  '🔒',
  '⚠️',
  '💡',
]

const RELATION_TYPES_QUERY = gql`
  query ProjectHorizontalRelationTypes($id: ID!) {
    project(id: $id) {
      id
      horizontalRelationTypes {
        id
        directTitle
        reverseTitle
        color
        icon
        horizontalRelationsCount
      }
    }
  }
`

const CREATE_RELATION_TYPE = gql`
  mutation CreateHorizontalRelationType(
    $projectId: ID!
    $directTitle: String!
    $reverseTitle: String!
    $color: String
    $icon: String
  ) {
    createHorizontalRelationType(
      projectId: $projectId
      directTitle: $directTitle
      reverseTitle: $reverseTitle
      color: $color
      icon: $icon
    ) {
      horizontalRelationType {
        id
        directTitle
        reverseTitle
        color
        icon
        horizontalRelationsCount
      }
      errors
    }
  }
`

const UPDATE_RELATION_TYPE = gql`
  mutation UpdateHorizontalRelationType(
    $id: ID!
    $directTitle: String
    $reverseTitle: String
    $color: String
    $icon: String
  ) {
    updateHorizontalRelationType(
      id: $id
      directTitle: $directTitle
      reverseTitle: $reverseTitle
      color: $color
      icon: $icon
    ) {
      horizontalRelationType {
        id
        directTitle
        reverseTitle
        color
        icon
        horizontalRelationsCount
      }
      errors
    }
  }
`

const DESTROY_RELATION_TYPE = gql`
  mutation DestroyHorizontalRelationType($id: ID!) {
    destroyHorizontalRelationType(id: $id) {
      horizontalRelationType {
        id
      }
      errors
    }
  }
`

const {
  load: loadRelationTypes,
  result: relationTypesResult,
  loading,
} = useLazyQuery(
  RELATION_TYPES_QUERY,
  () => ({
    id: props.projectId,
  }),
  {
    fetchPolicy: 'network-only',
  }
)

const { mutate: createRelationTypeMutation } = useMutation(CREATE_RELATION_TYPE)
const { mutate: updateRelationTypeMutation } = useMutation(UPDATE_RELATION_TYPE)
const { mutate: destroyRelationTypeMutation } = useMutation(DESTROY_RELATION_TYPE)

watch(relationTypesResult, data => {
  if (data?.project?.horizontalRelationTypes) {
    relationTypes.value = data.project.horizontalRelationTypes || []
  }
})

watch(
  () => props.expanded,
  expanded => {
    if (expanded && !relationTypesResult.value && !loading.value) {
      loadRelationTypes()
    }
  },
  { immediate: true }
)

const canCreate = computed(() => {
  return newRelationType.value.directTitle?.trim() && newRelationType.value.reverseTitle?.trim()
})

const canUpdate = computed(() => {
  return (
    editingRelationType.value?.directTitle?.trim() &&
    editingRelationType.value?.reverseTitle?.trim()
  )
})

function editRelationType(relationType) {
  editingRelationType.value = { ...relationType }
  showCreateModal.value = false
  showEditModal.value = true
  error.value = ''
}

function cancelNew() {
  showCreateModal.value = false
  newRelationType.value = { directTitle: '', reverseTitle: '', color: '#6b7280', icon: '' }
  error.value = ''
}

function cancelEdit() {
  showEditModal.value = false
  editingRelationType.value = null
  error.value = ''
}

function openCreateModal() {
  showEditModal.value = false
  error.value = ''
  showCreateModal.value = true
}

async function createRelationType() {
  if (!canCreate.value) return

  submitting.value = true
  error.value = ''

  try {
    const { data } = await createRelationTypeMutation({
      projectId: props.projectId,
      directTitle: newRelationType.value.directTitle.trim(),
      reverseTitle: newRelationType.value.reverseTitle.trim(),
      color: newRelationType.value.color || '#6b7280',
      icon: newRelationType.value.icon?.trim() || null,
    })

    if (data?.createHorizontalRelationType?.errors?.length) {
      error.value = data.createHorizontalRelationType.errors.join(', ')
    } else if (data?.createHorizontalRelationType?.horizontalRelationType) {
      // Add new item to local list immediately
      relationTypes.value = [
        ...relationTypes.value,
        data.createHorizontalRelationType.horizontalRelationType,
      ]
      cancelNew()
    }
  } catch (e) {
    error.value =
      s__('ProjectSettings|Failed to create relation type') +
      ': ' +
      (e.message || s__('ProjectSettings|Unknown error'))
  } finally {
    submitting.value = false
  }
}

async function updateRelationType() {
  if (!canUpdate.value || !editingRelationType.value) return

  submitting.value = true
  error.value = ''

  try {
    const { data } = await updateRelationTypeMutation({
      id: editingRelationType.value.id,
      directTitle: editingRelationType.value.directTitle.trim(),
      reverseTitle: editingRelationType.value.reverseTitle.trim(),
      color: editingRelationType.value.color || '#6b7280',
      icon: editingRelationType.value.icon?.trim() || null,
    })

    if (data?.updateHorizontalRelationType?.errors?.length) {
      error.value = data.updateHorizontalRelationType.errors.join(', ')
    } else if (data?.updateHorizontalRelationType?.horizontalRelationType) {
      // Update item in local list
      const updated = data.updateHorizontalRelationType.horizontalRelationType
      relationTypes.value = relationTypes.value.map(rt => (rt.id === updated.id ? updated : rt))
      cancelEdit()
    }
  } catch (e) {
    error.value =
      s__('ProjectSettings|Failed to update relation type') +
      ': ' +
      (e.message || s__('ProjectSettings|Unknown error'))
  } finally {
    submitting.value = false
  }
}

async function deleteRelationType(relationType) {
  const confirmMsg =
    relationType.horizontalRelationsCount > 0
      ? s__(
          'ProjectSettings|This will delete the relation type and all %{count} relations using it. Are you sure?'
        ).replace('%{count}', relationType.horizontalRelationsCount)
      : s__('ProjectSettings|Are you sure you want to delete this relation type?')

  if (!confirm(confirmMsg)) return

  try {
    const { data } = await destroyRelationTypeMutation({
      id: relationType.id,
    })

    if (data?.destroyHorizontalRelationType?.errors?.length) {
      alert(data.destroyHorizontalRelationType.errors.join(', '))
    } else {
      // Remove from local list
      relationTypes.value = relationTypes.value.filter(rt => rt.id !== relationType.id)
    }
  } catch (e) {
    alert(
      s__('ProjectSettings|Failed to delete relation type') +
        ': ' +
        (e.message || s__('ProjectSettings|Unknown error'))
    )
  }
}

defineExpose({
  openCreateModal,
})
</script>

<style scoped>
.relation-type-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 8px;
  min-width: 0;
}

.relation-type-main {
  flex: 1 1 auto;
  min-width: 0;
}

.relation-type-header {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.relation-reverse-line {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

.relation-link-arrow {
  flex-shrink: 0;
}

.relation-direct-badge,
.relation-reverse-badge {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.relation-count {
  font-size: 13px;
  margin-top: 4px;
}

.relation-type-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.relation-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
}

.relation-icon {
  font-size: 20px;
  line-height: 1;
}

.relation-type-form {
  padding: 0;
}

.row {
  display: flex;
  gap: 12px;
}

@media (max-width: 480px) {
  .row {
    flex-direction: column;
  }

  .relation-reverse-line {
    width: 100%;
    margin-top: 4px;
  }
}

.btn-sm {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Button styles inherit from forms.css */

.muted {
  color: #888;
}

.error {
  color: #dc2626;
  font-size: 14px;
}

label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 14px;
}

input[type='text'] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

input[type='color'] {
  width: 100%;
  height: 38px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
}

/* Button styles inherit from forms.css */

.icon-picker {
  width: 100%;
}

.icon-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.icon-option {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.icon-option:hover {
  background: #f5f5f5;
  border-color: #ddd;
}

.icon-option.selected {
  background: #2aabee;
  border-color: #2aabee;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.relation-type-modal-content {
  max-width: 760px;
}
</style>
